

import { useEffect, useState, useMemo } from 'react';
import '../assets/styles/Dashboard.css';
import Maps from '../components/Maps';

export default function Dashboard() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [dados, setDados] = useState({
    regioes: [],
    povoados: [],
    familias: [],
    culturas: [],
    producaoPorPovoado: [],
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE_URL}/regioes/`).then(r => r.json()),
      fetch(`${API_BASE_URL}/povoados/`).then(r => r.json()),
      fetch(`${API_BASE_URL}/familias/`).then(r => r.json()),
      fetch(`${API_BASE_URL}/culturas/`).then(r => r.json()),
      fetch(`${API_BASE_URL}/producao_por_povoado/`).then(r => r.json()),
    ])
      .then(([regioes, povoados, familias, culturas, producaoPorPovoado]) => {
        // Debug: logar as respostas recebidas
        console.log('API regioes:', regioes);
        console.log('API povoados:', povoados);
        console.log('API familias:', familias);
        console.log('API culturas:', culturas);
        console.log('API producaoPorPovoado:', producaoPorPovoado);
        // Aceita tanto array direto quanto objeto com propriedade 'data'
        // Novo padrão: array está em res.productId
        const getArray = (res) => Array.isArray(res) ? res : (Array.isArray(res?.productId) ? res.productId : []);
        setDados({
          regioes: getArray(regioes),
          povoados: getArray(povoados),
          familias: getArray(familias),
          culturas: getArray(culturas),
          producaoPorPovoado: getArray(producaoPorPovoado),
        });
        setLoading(false);
      })
      .catch(error => {
        console.error('Erro ao buscar dados:', error);
        setLoading(false);
      });
  }, [API_BASE_URL]);

  // KPIs dinâmicos
  const povoadosCount = dados.povoados?.length || 0;
  const culturasCount = dados.culturas?.length || 0;
  const familiasCount = dados.familias?.length || 0;
  const regioesCount = dados.regioes?.length || 0;
  // const usuariosCount = dados.usuarios?.length || 0;

  // Produção total (por povoado)
  const producaoTotal = useMemo(() => {
    return dados.producaoPorPovoado?.reduce((acc, item) => acc + (item.total_produzido || 0), 0) || 0;
  }, [dados.producaoPorPovoado]);

  // Tabela: produção por povoado/cultura
  const tableData = useMemo(() => {
    if (!Array.isArray(dados.producaoPorPovoado)) return [];
    return dados.producaoPorPovoado.map(item => ({
      povoado: item.nome_povoado || '',
      cultura: item.cultura || '',
      total: item.total_produzido || 0,
      unidade: item.unidade || 'kg',
      ano: item.ano || ''
    }));
  }, [dados.producaoPorPovoado]);

  // Lógica de paginação já está em tableData acima (producaoPorPovoado removido)

  const totalPages = Math.ceil(tableData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = tableData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="dashboard-main">
      {loading ? (
        <div className="modern-loading-container">
          <div className="loading-animation">
            <div className="pulse-ring"></div>
            <div className="pulse-ring delay-1"></div>
            <div className="pulse-ring delay-2"></div>
            <div className="loading-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" 
                  fill="currentColor"
                  className="sparkle-icon"
                />
              </svg>
            </div>
          </div>
          <div className="loading-text">
            <h3>Carregando Dashboard</h3>
            <p>Preparando seus dados agropecuários...</p>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="dashboard-header">
            <p>Painel Principal</p>
          </div>
          
          <div className='multContainer'>
          <div className="multContaineBox">
            <h2>Regiões</h2>
            <p className='NumberInfo'>{regioesCount}</p>
            <h4>Total de Regiões</h4>
          </div>
          <div className="multContaineBox">
            <h2>Povoados</h2>
            <p className='NumberInfo'>{povoadosCount}</p>
            <h4>Total de Povoados</h4>
          </div>
          <div className="multContaineBox">
            <h2>Famílias</h2>
            <p className='NumberInfo'>{familiasCount}</p>
            <h4>Total de Famílias</h4>
          </div>
          <div className="multContaineBox">
            <h2>Culturas</h2>
            <p className='NumberInfo'>{culturasCount}</p>
            <h4>Total de Culturas</h4>
          </div>
          <div className="multContaineBox">
            <h2>Produção Total</h2>
            <p className='NumberInfo'>{producaoTotal.toLocaleString('pt-BR')} kg</p>
            <h4>Produção (por povoado/cultura)</h4>
          </div>
          </div>

          <div className='dashbordContainer'>
            <div className="map-container">
              <Maps povoados={dados.povoados} />
            </div>
            
            <div className="map-container">
              <h3 className="dashboard-table-title">Produção por Povoado e Cultura</h3>
              <div className="dashboard-table-container">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Povoado</th>
                      <th>Cultura</th>
                      <th>Produção</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="table-empty-state">
                          Sem dados disponíveis para produção por povoado e cultura.
                        </td>
                      </tr>
                    ) : (
                      currentData.map((row, idx) => (
                        <tr key={row.povoado + row.cultura + row.ano + idx}>
                          <td>{row.povoado}</td>
                          <td>{row.cultura}</td>
                          <td>{row.total.toLocaleString('pt-BR')} {row.unidade}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                
                {/* Controles de Paginação */}
                {tableData.length > itemsPerPage && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      Mostrando {startIndex + 1}-{Math.min(endIndex, tableData.length)} de {tableData.length} itens
                    </div>
                    <div className="pagination-controls">
                      <button 
                        onClick={goToPrevious} 
                        disabled={currentPage === 1}
                        className="pagination-btn"
                      >
                        ‹ Anterior
                      </button>
                      
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button 
                        onClick={goToNext} 
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                      >
                        Próximo ›
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
